/* eslint-disable @bigbinary/neeto/hard-coded-strings-should-be-localized */

/**
 * Utility functions for generating field-level differences between objects.
 *
 * This module provides functions to compare two objects and generate
 * structured diff data that can be used with the FieldDiffViewer component.
 */

import { FieldDiff } from "@/components/ui/field-diff-viewer";

export type { FieldDiff };

export interface FieldConfig {
  label: string;
  type: FieldDiff["type"];
  exclude?: boolean;
  transform?: (value: any) => any;
}

export interface DiffConfig {
  fields: Record<string, FieldConfig>;
  excludeFields?: string[];
}

/**
 * Default field configuration for Source objects
 * Note: Labels are translation keys that should be localized in the consuming component
 */
export const sourceFieldConfig: DiffConfig = {
  fields: {
    title: { label: "common.label.title", type: "text" },
    institution: { label: "sources.label.institution", type: "text" },
    description: { label: "common.label.description", type: "text" },
    type: { label: "common.label.type", type: "text" },
    start_date: { label: "sources.label.start_date", type: "date" },
    end_date: { label: "sources.label.end_date", type: "date" },
    total_pages: { label: "sources.label.total_pages", type: "number" },
    file: { label: "common.label.file", type: "file" },
    requires_text_extraction: {
      label: "sources.label.requires_text_extraction",
      type: "boolean",
    },
    extracted_text: { label: "sources.label.extracted_text", type: "text" },
    use_bates_numbers: {
      label: "sources.label.use_bates_numbers",
      type: "boolean",
    },
    source_starting_bates_number: {
      label: "sources.label.starting_bates_number",
      type: "text",
    },
    corresponding_page_number: {
      label: "sources.label.corresponding_page_number",
      type: "text",
    },
    case: { label: "common.label.case", type: "number" },
  },
  excludeFields: [
    "id",
    "version",
    "created_date",
    "updated_date",
    "deleted_at",
    "created_by",
    "updated_by",
    "deleted_by",
    "file_uploaded_date",
    "ocr_task_status",
    "ocr_task_status_message",
    "ocr_task_id",
    "ai_summary_status",
    "ai_summary_data",
    "ai_summary_error_message",
    "ai_summary_error_traceback",
  ],
};

/**
 * Field configuration for AttorneyContact objects
 * Note: Labels are translation keys that should be localized in the consuming component
 */
export const attorneyContactFieldConfig: DiffConfig = {
  fields: {
    first_name: { label: "common.label.first_name", type: "text" },
    last_name: { label: "common.label.last_name", type: "text" },
    email: { label: "common.label.email", type: "text" },
    phone: { label: "common.label.phone", type: "text" },
    firm: { label: "common.label.firm", type: "text" },
    address: { label: "common.label.address", type: "text" },
    url: { label: "common.label.url", type: "text" },
  },
  excludeFields: [
    "id",
    "version",
    "created_date",
    "updated_date",
    "deleted_at",
    "created_by",
    "updated_by",
    "deleted_by",
  ],
};

/**
 * Field configuration for User objects
 * Note: Labels are translation keys that should be localized in the consuming component
 */
export const userFieldConfig: DiffConfig = {
  fields: {
    username: { label: "common.label.username", type: "text" },
    email: { label: "common.label.email", type: "text" },
    first_name: { label: "common.label.first_name", type: "text" },
    last_name: { label: "common.label.last_name", type: "text" },
    group: { label: "users.label.group", type: "text" },
  },
  excludeFields: [
    "id",
    "version",
    "password",
    "last_login",
    "is_active",
    "is_staff",
    "is_superuser",
    "date_joined",
    "created_date",
    "updated_date",
    "deleted_at",
    "created_by",
    "updated_by",
    "deleted_by",
    "groups",
  ],
};

/**
 * Field configuration for Civil Case objects
 * Note: Labels are translation keys that should be localized in the consuming component
 */
export const civilCaseFieldConfig: DiffConfig = {
  fields: {
    name: { label: "common.label.name", type: "text" },
    court: { label: "common.label.court", type: "text" },
    defendant: { label: "cases.label.defendant", type: "text" },
    plaintiff: { label: "cases.label.plaintiff", type: "text" },
    exact_trial_date: { label: "cases.label.exact_trial_date", type: "date" },
    inexact_trial_date: {
      label: "cases.label.inexact_trial_date",
      type: "text",
    },
    attorney_contact: { label: "cases.label.attorney_contact", type: "number" },
    internal_contact: { label: "cases.label.internal_contact", type: "number" },
    overview: { label: "cases.label.overview", type: "text" },
    folder: { label: "common.label.folder", type: "text" },
    archived: { label: "common.label.archived", type: "boolean" },
    lead_counsel: { label: "cases.label.lead_counsel", type: "number" },
    co_counsel: { label: "cases.label.co_counsel", type: "number" },
    opposing_counsel: { label: "cases.label.opposing_counsel", type: "number" },
    lawsuit_type: { label: "cases.label.lawsuit_type", type: "text" },
  },
  excludeFields: [
    "id",
    "version",
    "created_date",
    "updated_date",
    "deleted_at",
    "created_by",
    "updated_by",
    "deleted_by",
    "is_civil",
    "case_number",
    "default_tags",
    "custom_tags",
    "client",
    "subclass_case_object",
  ],
};

/**
 * Field configuration for Criminal Case objects
 * Note: Labels are translation keys that should be localized in the consuming component
 */
export const criminalCaseFieldConfig: DiffConfig = {
  fields: {
    name: { label: "common.label.name", type: "text" },
    court: { label: "common.label.court", type: "text" },
    defendant: { label: "cases.label.defendant", type: "text" },
    plaintiff: { label: "cases.label.plaintiff", type: "text" },
    exact_trial_date: { label: "cases.label.exact_trial_date", type: "date" },
    inexact_trial_date: {
      label: "cases.label.inexact_trial_date",
      type: "text",
    },
    attorney_contact: { label: "cases.label.attorney_contact", type: "number" },
    internal_contact: { label: "cases.label.internal_contact", type: "number" },
    overview: { label: "cases.label.overview", type: "text" },
    folder: { label: "common.label.folder", type: "text" },
    archived: { label: "common.label.archived", type: "boolean" },
    prosecutor: { label: "cases.label.prosecutor", type: "text" },
    current_charges: { label: "cases.label.current_charges", type: "text" },
  },
  excludeFields: [
    "id",
    "version",
    "created_date",
    "updated_date",
    "deleted_at",
    "created_by",
    "updated_by",
    "deleted_by",
    "is_civil",
    "case_number",
    "default_tags",
    "custom_tags",
    "client",
    "subclass_case_object",
  ],
};

/**
 * Field configuration for Case Timer objects
 * Note: Labels are translation keys that should be localized in the consuming component
 */
export const caseTimerFieldConfig: DiffConfig = {
  fields: {
    description: { label: "common.label.description", type: "text" },
    start_time: { label: "casetimer.label.start_time", type: "datetime" },
    end_time: { label: "casetimer.label.end_time", type: "datetime" },
    case_id: { label: "common.label.case", type: "number" },
  },
  excludeFields: [
    "id",
    "version",
    "created_date",
    "updated_date",
    "deleted_at",
    "created_by",
    "updated_by",
    "deleted_by",
    "user_id",
    "case",
    "user",
  ],
};

/**
 * Field configuration for Communication objects
 * Note: Labels are translation keys that should be localized in the consuming component
 */
export const communicationFieldConfig: DiffConfig = {
  fields: {
    person: { label: "communications.label.person", type: "text" },
    communication_type: { label: "common.label.type", type: "text" },
    start_date: { label: "common.label.date", type: "date" },
    duration_amount: {
      label: "communications.label.duration_amount",
      type: "number",
    },
    duration_unit: {
      label: "communications.label.duration_unit",
      type: "text",
    },
    public: { label: "communications.label.public", type: "boolean" },
    notes: { label: "common.label.notes", type: "text" },
  },
  excludeFields: [
    "id",
    "version",
    "created_date",
    "updated_date",
    "deleted_at",
    "created_by",
    "updated_by",
    "deleted_by",
    "case_id",
    "user_id",
    "case",
    "user",
  ],
};

/**
 * Field configuration for Deadline objects
 * Note: Labels are translation keys that should be localized in the consuming component
 */
export const deadlineFieldConfig: DiffConfig = {
  fields: {
    name: { label: "common.label.name", type: "text" },
    date: { label: "common.label.date", type: "date" },
    notes: { label: "common.label.notes", type: "text" },
    private: { label: "common.label.private", type: "boolean" },
    case: { label: "common.label.case", type: "number" },
  },
  excludeFields: [
    "id",
    "version",
    "created_date",
    "updated_date",
    "deleted_at",
    "created_by",
    "updated_by",
    "deleted_by",
  ],
};

/**
 * Field configuration for Event objects
 * Note: Labels are translation keys that should be localized in the consuming component
 */
export const eventFieldConfig: DiffConfig = {
  fields: {
    title: { label: "common.label.title", type: "text" },
    author: { label: "common.label.author", type: "text" },
    date: { label: "common.label.date", type: "date" },
    description: { label: "common.label.description", type: "text" },
    starting_page: { label: "common.label.start_page", type: "number" },
    ending_page: { label: "common.label.end_page", type: "number" },
    starting_bates_number: {
      label: "common.label.start_bates_number",
      type: "text",
    },
    ending_bates_number: {
      label: "common.label.end_bates_number",
      type: "text",
    },
    include_in_timeline: {
      label: "events.label.include_in_timeline",
      type: "boolean",
    },
    background_color: { label: "events.label.background_color", type: "text" },
    source_id: { label: "common.label.source", type: "number" },
  },
  excludeFields: [
    "id",
    "version",
    "created_date",
    "updated_date",
    "deleted_at",
    "created_by",
    "updated_by",
    "deleted_by",
    "case_tag_ids",
    "case_tags",
    "source",
  ],
};

/**
 * Field configuration for Note objects
 * Note: Labels are translation keys that should be localized in the consuming component
 */
export const noteFieldConfig: DiffConfig = {
  fields: {
    title: { label: "common.label.title", type: "text" },
    author: { label: "common.label.author", type: "text" },
    date: { label: "common.label.date", type: "date" },
    private: { label: "common.label.private", type: "boolean" },
    description: { label: "common.label.description", type: "text" },
    starting_page: { label: "common.label.start_page", type: "number" },
    ending_page: { label: "common.label.end_page", type: "number" },
    starting_bates_number: {
      label: "common.label.start_bates_number",
      type: "text",
    },
    ending_bates_number: {
      label: "common.label.end_bates_number",
      type: "text",
    },
    source_id: { label: "common.label.source", type: "number" },
    file: { label: "common.label.file", type: "file" },
  },
  excludeFields: [
    "id",
    "version",
    "created_date",
    "updated_date",
    "deleted_at",
    "created_by",
    "updated_by",
    "deleted_by",
    "case_id",
    "source",
    "associated_note",
    "case",
    "user",
  ],
};

/**
 * Field configuration for Pleading objects
 * Note: Labels are translation keys that should be localized in the consuming component
 */
export const pleadingFieldConfig: DiffConfig = {
  fields: {
    title: { label: "common.label.title", type: "text" },
    pleading_type: { label: "common.label.type", type: "text" },
    party: { label: "pleadings.label.party", type: "text" },
    filing_date: { label: "pleadings.label.filing_date", type: "date" },
    due_date: { label: "pleadings.label.due_date", type: "date" },
    response_necessary: {
      label: "pleadings.label.response_necessary",
      type: "boolean",
    },
    associated_pleading: {
      label: "pleadings.label.associated_pleading",
      type: "text",
    },
  },
  excludeFields: [
    "id",
    "version",
    "created_date",
    "updated_date",
    "deleted_at",
    "created_by",
    "updated_by",
    "deleted_by",
    "case_id",
    "case",
  ],
};

/**
 * Field configuration for Research Document objects
 * Note: Labels are translation keys that should be localized in the consuming component
 */
export const researchDocumentFieldConfig: DiffConfig = {
  fields: {
    title: { label: "common.label.title", type: "text" },
    description: { label: "common.label.description", type: "text" },
    article: { label: "research.label.article", type: "text" },
    journal_name: { label: "research.label.journal_name", type: "text" },
    volume: { label: "research.label.volume", type: "text" },
    supplement_or_issue: {
      label: "research.label.supplement_or_issue",
      type: "text",
    },
    year: { label: "research.label.year", type: "text" },
    page_from: { label: "common.label.start_page", type: "text" },
    page_to: { label: "common.label.end_page", type: "text" },
    retrieved_url: { label: "research.label.retrieved_url", type: "text" },
    private: {
      label: "common.label.private",
      type: "boolean",
    },
    case_ids: {
      label: "common.label.cases",
      type: "text",
    },
    requires_text_extraction: {
      label: "common.label.requires_text_extraction",
      type: "boolean",
    },
    apa_citation: { label: "research.label.apa_citation", type: "text" },
    bluebook_citation: {
      label: "research.label.bluebook_citation",
      type: "text",
    },
    file: { label: "common.label.file", type: "file" },
  },
  excludeFields: [
    "id",
    "version",
    "created_date",
    "updated_date",
    "deleted_at",
    "created_by",
    "updated_by",
    "deleted_by",
    "case_id",
    "folder_id",
    "case",
    "folder",
  ],
};

/**
 * Field configuration for Research Folder objects
 * Note: Labels are translation keys that should be localized in the consuming component
 */
export const researchFolderFieldConfig: DiffConfig = {
  fields: {
    name: { label: "research.label.folder_name", type: "text" },
    public: {
      label: "research.label.accessible_by_everyone",
      type: "boolean",
    },
  },
  excludeFields: [
    "id",
    "version",
    "created_date",
    "updated_date",
    "deleted_at",
    "created_by",
    "updated_by",
    "deleted_by",
  ],
};

/**
 * Compare two values for equality, handling various data types
 */
export function valuesEqual(a: any, b: any): boolean {
  // Handle null/undefined
  if (a === null || a === undefined) {
    return b === null || b === undefined;
  }
  if (b === null || b === undefined) {
    return false;
  }

  // Handle dates
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }
  if (a instanceof Date || b instanceof Date) {
    return false;
  }

  // Handle files
  if (a instanceof File && b instanceof File) {
    return (
      a.name === b.name &&
      a.size === b.size &&
      a.lastModified === b.lastModified
    );
  }
  if (a instanceof File || b instanceof File) {
    return false;
  }

  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => valuesEqual(item, b[index]));
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    return false;
  }

  // Handle objects
  if (typeof a === "object" && typeof b === "object") {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((key) => valuesEqual(a[key], b[key]));
  }

  // Handle primitives
  return a === b;
}

/**
 * Transform a value based on field configuration
 */
export function transformValue(value: any, config: FieldConfig): any {
  if (config.transform) {
    return config.transform(value);
  }
  return value;
}

/**
 * Generate field differences between two objects
 */
export function generateFieldDiffs<T extends Record<string, any>>(
  localData: T,
  serverData: T,
  config: DiffConfig = sourceFieldConfig,
): FieldDiff[] {
  const diffs: FieldDiff[] = [];
  const allFields = Array.from(
    new Set([
      ...Object.keys(localData || {}),
      ...Object.keys(serverData || {}),
    ]),
  );

  for (const field of allFields) {
    // Skip excluded fields
    if (config.excludeFields?.includes(field)) {
      continue;
    }

    // Get field configuration
    const fieldConfig = config.fields[field];
    if (!fieldConfig || fieldConfig.exclude) {
      continue;
    }

    // Get values
    const localValue = localData?.[field];
    const serverValue = serverData?.[field];

    // Transform values
    const transformedLocal = transformValue(localValue, fieldConfig);
    const transformedServer = transformValue(serverValue, fieldConfig);

    // Check if values are different
    const hasChanged = !valuesEqual(transformedLocal, transformedServer);

    diffs.push({
      field,
      label: fieldConfig.label,
      localValue: transformedLocal,
      serverValue: transformedServer,
      hasChanged,
      type: fieldConfig.type,
    });
  }

  return diffs;
}

/**
 * Generate field differences specifically for Source objects
 */
export function generateSourceFieldDiffs(
  localSource: any,
  serverSource: any,
): FieldDiff[] {
  return generateFieldDiffs(localSource, serverSource, sourceFieldConfig);
}

/**
 * Create a simplified diff summary for display
 */
export function createDiffSummary(diffs: FieldDiff[]): {
  totalFields: number;
  changedFields: number;
  addedFields: number;
  removedFields: number;
  modifiedFields: number;
} {
  const changedDiffs = diffs.filter((diff) => diff.hasChanged);

  const addedFields = changedDiffs.filter((diff) => {
    const hasLocal =
      diff.localValue !== null &&
      diff.localValue !== undefined &&
      diff.localValue !== "";
    const hasServer =
      diff.serverValue !== null &&
      diff.serverValue !== undefined &&
      diff.serverValue !== "";
    return hasLocal && !hasServer;
  }).length;

  const removedFields = changedDiffs.filter((diff) => {
    const hasLocal =
      diff.localValue !== null &&
      diff.localValue !== undefined &&
      diff.localValue !== "";
    const hasServer =
      diff.serverValue !== null &&
      diff.serverValue !== undefined &&
      diff.serverValue !== "";
    return !hasLocal && hasServer;
  }).length;

  const modifiedFields = changedDiffs.length - addedFields - removedFields;

  return {
    totalFields: diffs.length,
    changedFields: changedDiffs.length,
    addedFields,
    removedFields,
    modifiedFields,
  };
}
